/** @jsxImportSource @emotion/react */
import { zodResolver } from '@hookform/resolvers/zod'
import {
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Container,
  FormControl,
  FormControlLabel,
  FormHelperText,
  InputAdornment,
  InputLabel,
  LinearProgress,
  MenuItem,
  Select,
  Step,
  StepLabel,
  Stepper,
  TextField,
  Typography,
} from '@mui/material'
import * as Sentry from '@sentry/react'
import { useEffect, useRef, useState } from 'react'
import { Controller, useForm } from 'react-hook-form'

import { generateS3Url } from '@app/utils/strings.utils'
import { useDataUpload } from '@app/modules/Home/Data/hooks/useDataUpload'
import {
  ALLOWED_METRIC_TYPES,
  DataUploadFormData,
  DataUploadSchema,
  FieldConfig,
  GdalLayerInfo,
} from '@app/modules/Home/Data/types'
import { extractGdalInfo } from '@app/modules/Home/Data/utils/gdal.utils'
import { groupFiles } from '@app/modules/Home/Data/utils/fileGroup.utils'
import { OGR_ACCEPT_ATTR } from '@app/modules/Home/Data/constants/ogr.formats'
import { Banner } from '@app/components/Guide/ui/Banner'
import { toast } from '@app/components/ui/Toast/toast'
import ElementAttributesStep from './ElementAttributesStep'
import {
  pageWrapper,
  container,
  formWrapper,
  formOuter,
  formContainer,
  fileRow,
} from './DataUpload.styles'

type IconOption = { name: string; filename: string }

const toDisplayLabel = (name: string) =>
  name.replace(/[-_]/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())

const TEXT_FIELDS: FieldConfig[] = [
  { name: 'name', label: 'Dataset name' },
  { name: 'description', label: 'Description', textbox: true },
  { name: 'type', label: 'Type' },
  { name: 'source', label: 'Source' },
  { name: 'spatialExtent', label: 'Spatial Extent' },
  { name: 'icon', label: 'Icon' },
  { name: 'lineSegmentLength', label: 'Split Line Segment Length (m)', numeric: true },
  { name: 'polygonSegmentArea', label: 'Split Polygon Segment Area (m²)', numeric: true },
  { name: 'unit', label: 'Unit of Measurement (ha, km, buildings, etc)' },
]

const STEPS = ['Upload Element Dataset', 'Configure Element Attributes']

const HomeDataUpload = () => {
  const inputRef = useRef<HTMLInputElement>(null)
  const { upload, uploading, progress, status, errorMessage } = useDataUpload()
  const [iconOptions, setIconOptions] = useState<IconOption[]>([])
  const [activeStep, setActiveStep] = useState(0)
  const [gdalInfo, setGdalInfo] = useState<GdalLayerInfo | null>(null)
  const [gdalFailed, setGdalFailed] = useState(false)
  const [gdalSkipped, setGdalSkipped] = useState(false)
  const [datasetName, setDatasetName] = useState<string>('')
  const [extracting, setExtracting] = useState(false)

  useEffect(() => {
    fetch('/data/dataset_icons.csv')
      .then((r) => r.text())
      .then((text) => {
        const options = text
          .trim()
          .split('\n')
          .slice(1)
          .filter((line) => line.endsWith('.svg'))
          .map((line) => {
            const [name, filename] = line.split(',')
            return { name, filename }
          })
        setIconOptions(options)
      })
  }, [])

  const {
    register,
    handleSubmit,
    reset,
    watch,
    control,
    resetField,
    getValues,
    formState: { errors, isSubmitting },
  } = useForm<DataUploadFormData>({
    resolver: zodResolver(DataUploadSchema),
  })

  const fileList = watch('file')
  const selectedFiles = fileList instanceof FileList ? Array.from(fileList) : []
  const geometryType = watch('geometryType')

  const handleGeometryChange = (
    field: { onChange: (v: typeof geometryType | undefined) => void },
    value: string,
  ) => {
    const newGeometry = value === '' ? undefined : (value as typeof geometryType)
    field.onChange(newGeometry)
    const currentMetricType = getValues('metricType')
    if (
      newGeometry &&
      currentMetricType &&
      !ALLOWED_METRIC_TYPES[newGeometry].includes(currentMetricType)
    ) {
      resetField('metricType')
    }
  }

  const handleReset = () => {
    reset()
    if (inputRef.current) inputRef.current.value = ''
    setGdalInfo(null)
    setGdalFailed(false)
    setGdalSkipped(false)
    setExtracting(false)
    setActiveStep(0)
    setDatasetName('')
  }

  const onSubmit = async (data: DataUploadFormData) => {
    const { primary, allFiles } = groupFiles(Array.from(data.file))
    try {
      setGdalInfo(null)
      setGdalFailed(false)
      setGdalSkipped(false)

      const MAX_GDAL_SIZE = 2.5 * 1024 ** 3
      const fileTooLarge = primary.size > MAX_GDAL_SIZE
      if (!fileTooLarge) setExtracting(true)
      const [uploadResult, gdalResult] = await Promise.allSettled([
        upload(allFiles, {
          name: data.name,
          description: data.description,
          source: data.source || undefined,
          spatialExtent: data.spatialExtent || undefined,
          icon: data.icon ? `s3://${data.icon}` : undefined,
          displaySource: data.displaySource || undefined,
          type: data.type || undefined,
          utility: data.utility || undefined,
          geometryType: data.geometryType,
          lineSegmentLength: data.lineSegmentLength,
          polygonSegmentArea: data.polygonSegmentArea,
          changeToPoint: data.changeToPoint,
          unit: data.unit || undefined,
          metricType: data.metricType,
          license: data.license,
        }),
        !fileTooLarge ? extractGdalInfo(allFiles) : Promise.resolve(null),
      ])

      setExtracting(false)

      if (uploadResult.status === 'rejected') {
        Sentry.captureException(uploadResult.reason)
        toast({ title: 'Upload failed', type: 'error', position: 'top-right', duration: 8000 })
        return
      }

      if (fileTooLarge) {
        setGdalSkipped(true)
      } else if (gdalResult.status === 'fulfilled' && gdalResult.value !== null) {
        setGdalInfo(gdalResult.value)
      } else if (gdalResult.status === 'rejected') {
        Sentry.captureException(gdalResult.reason)
        setGdalFailed(true)
      }

      toast({ title: 'Upload complete', type: 'success', position: 'top-right', duration: 8000 })
      setDatasetName(data.name)
      setActiveStep(1)
    } catch (e) {
      Sentry.captureException(e)
    }
  }

  const { ref: fileRef, ...fileRegisterProps } = register('file')

  return (
    <Box css={pageWrapper}>
      <Box css={container}>
        <Banner header="Element Data Upload" subheader="Upload file based element datasets" />
        <Box css={formWrapper}>
          <Container>
            <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
              {STEPS.map((label) => (
                <Step key={label}>
                  <StepLabel>{label}</StepLabel>
                </Step>
              ))}
            </Stepper>

            {activeStep === 1 ? (
              <ElementAttributesStep
                gdalInfo={gdalInfo}
                gdalFailed={gdalFailed}
                gdalSkipped={gdalSkipped}
                onReset={handleReset}
                datasetName={datasetName}
              />
            ) : (
              <Box component="form" css={formOuter} onSubmit={handleSubmit(onSubmit)}>
                <Box css={fileRow}>
                  <Button
                    component="label"
                    variant="outlined"
                    color={errors.file ? 'error' : 'primary'}
                  >
                    {selectedFiles.length > 0 ? 'Change file(s)' : 'Choose file(s)'}
                    <input
                      {...fileRegisterProps}
                      ref={(el) => {
                        fileRef(el)
                        ;(inputRef as React.MutableRefObject<HTMLInputElement | null>).current = el
                      }}
                      type="file"
                      multiple
                      accept={OGR_ACCEPT_ATTR}
                      style={{ display: 'none' }}
                    />
                  </Button>
                  {selectedFiles.length > 0 ? (
                    <Box>
                      <Typography variant="body2" color="text.secondary">
                        {selectedFiles.length === 1
                          ? selectedFiles[0].name
                          : `${selectedFiles.length} files selected`}
                      </Typography>
                      {selectedFiles.length > 1 && (
                        <Box component="ul" sx={{ m: 0, pl: 2 }}>
                          {selectedFiles.map((f) => (
                            <Typography
                              key={f.name}
                              component="li"
                              variant="caption"
                              color="text.secondary"
                            >
                              {f.name}
                            </Typography>
                          ))}
                        </Box>
                      )}
                      {errors.file && (
                        <Typography variant="body2" color="error.main">
                          {errors.file.message as string}
                        </Typography>
                      )}
                    </Box>
                  ) : errors.file ? (
                    <Typography variant="body2" color="error.main">
                      {errors.file.message as string}
                    </Typography>
                  ) : null}
                </Box>
                <Box css={formContainer}>
                  {TEXT_FIELDS.map(({ name, label, textbox, numeric }) => {
                    if (name === 'icon') {
                      return (
                        <Controller
                          key={name}
                          name="icon"
                          control={control}
                          render={({ field }) => {
                            const selected =
                              iconOptions.find((o) => o.filename === field.value) ?? null
                            return (
                              <Autocomplete
                                options={iconOptions}
                                getOptionLabel={(o) => toDisplayLabel(o.name)}
                                value={selected}
                                onChange={(_, option) =>
                                  field.onChange(option ? option.filename : '')
                                }
                                renderOption={(props, option) => (
                                  <Box
                                    component="li"
                                    {...props}
                                    sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
                                  >
                                    <img
                                      src={generateS3Url(`s3://${option.filename}`, {
                                        preserveCase: true,
                                      })}
                                      width={20}
                                      height={20}
                                      alt=""
                                    />
                                    {toDisplayLabel(option.name)}
                                  </Box>
                                )}
                                renderInput={(params) => (
                                  <TextField
                                    {...params}
                                    label={label}
                                    error={!!errors.icon}
                                    helperText={errors.icon?.message as string | undefined}
                                    slotProps={{
                                      input: {
                                        ...params.InputProps,
                                        startAdornment: field.value ? (
                                          <>
                                            <InputAdornment position="start" sx={{ ml: 1 }}>
                                              <img
                                                src={generateS3Url(`s3://${field.value}`, {
                                                  preserveCase: true,
                                                })}
                                                width={20}
                                                height={20}
                                                alt=""
                                              />
                                            </InputAdornment>
                                            {params.InputProps.startAdornment}
                                          </>
                                        ) : (
                                          params.InputProps.startAdornment
                                        ),
                                      },
                                    }}
                                  />
                                )}
                              />
                            )
                          }}
                        />
                      )
                    }
                    return (
                      <TextField
                        key={name}
                        label={label}
                        fullWidth
                        multiline={textbox}
                        rows={textbox ? 3 : undefined}
                        type={numeric ? 'number' : undefined}
                        error={!!errors[name]}
                        helperText={errors[name]?.message as string | undefined}
                        {...register(
                          name,
                          numeric // treat empty string as undefined for optional numeric fields
                            ? {
                                setValueAs: (v) =>
                                  v === '' || isNaN(Number(v)) ? undefined : Number(v),
                              }
                            : {},
                        )}
                      />
                    )
                  })}
                  <Controller
                    name="geometryType"
                    control={control}
                    render={({ field }) => (
                      <FormControl fullWidth error={!!errors.geometryType}>
                        <InputLabel>Geometry Type</InputLabel>
                        <Select
                          label="Geometry Type"
                          {...field}
                          value={field.value ?? ''}
                          onChange={(e) => handleGeometryChange(field, e.target.value as string)}
                        >
                          <MenuItem value="line">Line</MenuItem>
                          <MenuItem value="point">Point</MenuItem>
                          <MenuItem value="polygon">Polygon</MenuItem>
                        </Select>
                        {errors.geometryType && (
                          <FormHelperText>{errors.geometryType.message}</FormHelperText>
                        )}
                      </FormControl>
                    )}
                  />
                  <Controller
                    name="metricType"
                    control={control}
                    render={({ field }) => {
                      // when no geometry is selected, show all options
                      const allowed = geometryType
                        ? ALLOWED_METRIC_TYPES[geometryType]
                        : (['count', 'area', 'length'] as const)
                      return (
                        <FormControl fullWidth error={!!errors.metricType}>
                          <InputLabel>Metric Type</InputLabel>
                          <Select
                            label="Metric Type"
                            {...field}
                            value={field.value ?? ''}
                            onChange={(e) => {
                              const val = e.target.value as string
                              field.onChange(val === '' ? undefined : val)
                            }}
                          >
                            {allowed.includes('count') && <MenuItem value="count">Count</MenuItem>}
                            {allowed.includes('area') && <MenuItem value="area">Area</MenuItem>}
                            {allowed.includes('length') && (
                              <MenuItem value="length">Length</MenuItem>
                            )}
                          </Select>
                          {errors.metricType && (
                            <FormHelperText>{errors.metricType.message}</FormHelperText>
                          )}
                        </FormControl>
                      )
                    }}
                  />
                  <Controller
                    name="license"
                    control={control}
                    render={({ field }) => (
                      <FormControl fullWidth error={!!errors.license}>
                        <InputLabel>License</InputLabel>
                        <Select
                          label="License"
                          {...field}
                          value={field.value ?? ''}
                          onChange={(e) => {
                            const val = e.target.value as string
                            field.onChange(val === '' ? undefined : val)
                          }}
                        >
                          <MenuItem value="Restricted">Restricted</MenuItem>
                          <MenuItem value="Controlled">Controlled</MenuItem>
                          <MenuItem value="Open">Open</MenuItem>
                        </Select>
                        {errors.license && (
                          <FormHelperText>{errors.license.message}</FormHelperText>
                        )}
                      </FormControl>
                    )}
                  />
                  <Controller
                    name="changeToPoint"
                    control={control}
                    render={({ field }) => (
                      <FormControlLabel
                        control={<Checkbox checked={!!field.value} onChange={field.onChange} />}
                        label="Change to Point"
                      />
                    )}
                  />
                </Box>
                <Button
                  type="submit"
                  variant="contained"
                  disabled={uploading || isSubmitting}
                  sx={{ alignSelf: 'flex-start' }}
                >
                  {uploading ? 'Uploading…' : extracting ? 'Extracting attributes…' : 'Upload'}
                </Button>
                {uploading && <LinearProgress variant="determinate" value={progress} />}
                {extracting && !uploading && <LinearProgress variant="indeterminate" />}
                {status === 'error' && (
                  <Typography variant="body2" color="error.main">
                    {errorMessage}
                  </Typography>
                )}
              </Box>
            )}
          </Container>
        </Box>
      </Box>
    </Box>
  )
}

export default HomeDataUpload
